import { useState } from 'react';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  useOnboardingForm,
  type FieldConfig,
} from '@/components/smartai/ComponentOnboarding/useOnboardingForm';
import {
  useGetTestPlansMutation,
  useGetTestSuiteDetailMutation,
  type TestPlan,
  type TestSuite,
} from '@/components/smartai/api/testIntelligence';
import type { ScmProviderId } from '@/types/scm-provider';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface MapTestCasesModalProps {
  isOpen: boolean;
  onClose: () => void;
  org: string;
  project: string;
  thresholdValue: number;
  onThresholdChange: (v: number) => void;
  mapTestPatToken: string;
  provider?: ScmProviderId;
  onRunMapping: (args: {
    planId: string;
    suiteId: string;
    patToken: string;
  }) => void;
  onFetchJira?: (args: {
    organization: string;
    project: string;
    jiraEmail: string;
    jiraApiToken: string;
  }) => void;
}

// ── Zod schema ────────────────────────────────────────────────────────────────

const schema = z.object({
  patToken: z.string().min(1, 'PAT token is required'),
  planId: z.string().min(1, 'Select a test plan'),
  suiteId: z.string().min(1, 'Enter a suite ID'),
  threshold: z.number().min(0).max(100),
});

type ModalFormValues = z.infer<typeof schema>;

const jiraSchema = z.object({
  jiraEmail: z.string().min(1, 'Email is required'),
  jiraApiToken: z.string().min(1, 'Jira token is required'),
  organization: z.string().min(1, 'Enter a Jira organization'),
  project: z.string().min(1, 'Enter your Jira project key'),
  threshold: z.number().min(0).max(100),
});

type JiraFormValues = z.infer<typeof jiraSchema>;

const JiraMapTestCasesModal = ({
  isOpen,
  onClose,
  thresholdValue,
  onThresholdChange,
  onFetchJira,
}: MapTestCasesModalProps) => {
  const fields: FieldConfig<JiraFormValues>[] = [
    {
      name: 'jiraEmail',
      label: 'Email',
      type: 'text',
      placeholder: 'you@company.com',
    },
    {
      name: 'jiraApiToken',
      label: 'Jira token',
      type: 'password',
      placeholder: 'Enter your Jira API token',
    },
    {
      name: 'organization',
      label: 'Organization',
      type: 'text',
      placeholder: 'your-org (from your-org.atlassian.net)',
    },
    {
      name: 'project',
      label: 'Jira project key',
      type: 'text',
      placeholder: 'e.g. QA or MYAPP',
    },
    {
      name: 'threshold',
      label: 'Similarity Threshold (0–100)',
      type: 'number',
      placeholder: '75',
    },
  ];

  const { renderFields, onSubmit } = useOnboardingForm<JiraFormValues>({
    fields,
    defaultValues: {
      jiraEmail: '',
      jiraApiToken: '',
      organization: '',
      project: '',
      threshold: thresholdValue,
    },
    schema: jiraSchema,
    onSubmit: data => {
      onThresholdChange(data.threshold);
      onFetchJira?.({
        organization: data.organization.trim(),
        project: data.project.trim(),
        jiraEmail: data.jiraEmail.trim(),
        jiraApiToken: data.jiraApiToken.trim(),
      });
    },
  });

  return (
    <Dialog open={isOpen} onOpenChange={open => !open && onClose()}>
      <DialogContent className="max-w-[min(600px,92vw)]">
        <DialogHeader>
          <DialogTitle>Map Test Cases</DialogTitle>
        </DialogHeader>

        <form className="flex flex-col gap-4 py-2" onSubmit={onSubmit}>
          {renderFields()}
        </form>

        <DialogFooter className="flex justify-end gap-3">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={onSubmit as () => void}>
            Map & Run Jira Test Cases
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ── Component ─────────────────────────────────────────────────────────────────

const AdoMapTestCasesModal = ({
  isOpen,
  onClose,
  org,
  project,
  thresholdValue,
  onThresholdChange,
  mapTestPatToken,
  onRunMapping,
}: MapTestCasesModalProps) => {
  const [testPlans, setTestPlans] = useState<TestPlan[]>([]);
  const [confirmedSuite, setConfirmedSuite] = useState<TestSuite | null>(null);
  const [suiteError, setSuiteError] = useState('');

  const { mutate: fetchTestPlans, isPending: plansPending } =
    useGetTestPlansMutation();

  const { mutate: fetchSuiteDetail, isPending: suitePending } =
    useGetTestSuiteDetailMutation();

  const fields: FieldConfig<ModalFormValues>[] = [
    {
      name: 'patToken',
      label: 'PAT Token',
      type: 'password',
      placeholder: 'Enter your PAT',
      actionButton: (
        <Button
          onClick={() => {
            const pat = watch('patToken');
            if (!pat) return;
            fetchTestPlans(
              { organization: org, project, _pat: pat },
              {
                onSuccess: res => setTestPlans(res.plans),
              },
            );
          }}>
          {plansPending ? 'Fetching...' : 'Connect ADO Test Cases'}
        </Button>
      ),
      loading: plansPending,
    },
    {
      name: 'planId',
      label: 'Test Plan',
      type: 'combobox',
      placeholder: 'Select a test plan',
      options: testPlans.map(p => ({
        value: String(p.id),
        label: `${p.id} – ${p.name}`,
      })),
      disabled: testPlans.length === 0,
    },
    {
      name: 'suiteId',
      label: 'Test Suite ID',
      type: 'text',
      placeholder: 'Enter suite ID',
      actionButton: (
        <Button
          variant="secondary"
          onClick={() => {
            const suiteId = watch('suiteId');
            const planId = watch('planId');
            const pat = watch('patToken');
            setSuiteError('');
            setConfirmedSuite(null);
            fetchSuiteDetail(
              {
                organization: org,
                project,
                planId,
                suiteId: Number(suiteId),
                _pat: pat,
              },
              {
                onSuccess: res => setConfirmedSuite(res.suite),
                onError: () => setSuiteError('Suite not found — check the ID'),
              },
            );
          }}>
          {suitePending ? 'Fetching...' : 'Get Test Suite'}
        </Button>
      ),
      loading: suitePending,
    },
    {
      name: 'threshold',
      label: 'Similarity Threshold (0–100)',
      type: 'number',
      placeholder: '75',
    },
  ];

  const { renderFields, onSubmit, watch } = useOnboardingForm<ModalFormValues>({
    fields,
    defaultValues: {
      patToken: mapTestPatToken,
      planId: '',
      suiteId: '',
      threshold: thresholdValue,
    },
    schema,
    onSubmit: data => {
      onThresholdChange(data.threshold);
      onRunMapping({
        planId: data.planId,
        suiteId: data.suiteId,
        patToken: data.patToken,
      });
    },
  });

  return (
    <Dialog open={isOpen} onOpenChange={open => !open && onClose()}>
      <DialogContent className="max-w-[min(600px,92vw)]">
        <DialogHeader>
          <DialogTitle>Map Test Cases</DialogTitle>
        </DialogHeader>

        <form className="flex flex-col gap-4 py-2" onSubmit={onSubmit}>
          {renderFields()}

          {confirmedSuite && (
            <p className="mt-1 text-sm text-[var(--color-success,#2e7d32)]">
              Suite confirmed:{' '}
              <strong>
                {confirmedSuite.id} – {confirmedSuite.name}
              </strong>
            </p>
          )}

          {suiteError && (
            <p className="mt-1 text-sm text-[var(--color-error,#c62828)]">
              {suiteError}
            </p>
          )}
        </form>

        <DialogFooter className="flex justify-end gap-3">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={onSubmit as () => void}>Run Mapping</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const MapTestCasesModal = (props: MapTestCasesModalProps) =>
  props.provider === 'github' ? (
    <JiraMapTestCasesModal {...props} />
  ) : (
    <AdoMapTestCasesModal {...props} />
  );

export default MapTestCasesModal;
